// SPDX-License-Identifier: AGPL-3.0-or-later

import {IconContext} from '@phosphor-icons/react/dist/lib/context';
import type {IconProps, IconWeight} from '@phosphor-icons/react/dist/lib/types';
import React, {createElement, type ReactNode, useContext, useId} from 'react';

type SolarStyle = 'bold' | 'linear';

interface OpenElement {
	tag: string;
	props: Record<string, string>;
	children: Array<ReactNode>;
}

const SVG_TAG = /<(\/?)(\w+)([^>]*?)(\/?)>/g;
const SVG_ATTRIBUTE = /([\w-]+)="([^"]*)"/g;
const SLASH_LENGTH = 17 * Math.SQRT2;
const SLASH_STROKE: Record<SolarStyle, {line: number; gap: number}> = {
	bold: {line: 2, gap: 1.5},
	linear: {line: 1.5, gap: 1.25},
};

function solarStyleForWeight(weight: IconWeight): SolarStyle {
	return weight === 'fill' || weight === 'duotone' ? 'bold' : 'linear';
}

function reactAttributeName(name: string): string {
	return name.replace(/-([a-z])/g, (_match, letter: string) => letter.toUpperCase());
}

function parseSolarMarkup(markup: string): Array<ReactNode> {
	const root: Array<ReactNode> = [];
	const open: Array<OpenElement> = [];
	let key = 0;
	for (const [, closing, tag, attributes, selfClosing] of markup.matchAll(SVG_TAG)) {
		if (closing) {
			const element = open.pop();
			if (element)
				(open[open.length - 1]?.children ?? root).push(createElement(element.tag, element.props, ...element.children));
			continue;
		}
		const props: Record<string, string> = {key: String(key++)};
		for (const [, name, value] of attributes.matchAll(SVG_ATTRIBUTE)) props[reactAttributeName(name)] = value;
		if (selfClosing) (open[open.length - 1]?.children ?? root).push(createElement(tag, props));
		else open.push({tag, props, children: []});
	}
	return root;
}

function slashBar(length: number, width: number, fill?: string) {
	return createElement('rect', {
		x: 12 - length / 2,
		y: 12 - width / 2,
		width: length,
		height: width,
		rx: width / 2,
		transform: 'rotate(45 12 12)',
		fill,
	});
}

function SlashedGlyph({style, glyph}: {style: SolarStyle; glyph: Array<ReactNode>}) {
	const maskId = useId();
	const {line, gap} = SLASH_STROKE[style];
	return createElement(
		React.Fragment,
		null,
		createElement(
			'mask',
			{id: maskId, maskUnits: 'userSpaceOnUse', x: 0, y: 0, width: 24, height: 24},
			createElement('rect', {width: 24, height: 24, fill: '#fff'}),
			slashBar(SLASH_LENGTH + gap * 2, line + gap * 2, '#000'),
		),
		createElement('g', {mask: `url(#${maskId})`}, glyph),
		slashBar(SLASH_LENGTH, line),
	);
}

export function createSolarIcon(displayName: string, bold: string, linear: string, slashed = false) {
	const glyphs: Partial<Record<SolarStyle, Array<ReactNode>>> = {};
	const glyphFor = (style: SolarStyle) => {
		glyphs[style] ??= parseSolarMarkup(style === 'bold' ? bold : linear);
		return glyphs[style];
	};
	const SolarIcon = React.forwardRef<SVGSVGElement, React.PropsWithChildren<IconProps>>(
		({alt, children, color, size, weight, mirrored, ...props}, ref) => {
			const {
				color: contextColor = 'currentColor',
				size: contextSize,
				weight: contextWeight = 'regular',
				mirrored: contextMirrored = false,
				...contextProps
			} = useContext(IconContext);
			const style = solarStyleForWeight(weight ?? contextWeight);
			const fill = color ?? contextColor;
			const dimension = size ?? contextSize;
			const glyph = glyphFor(style);
			return createElement(
				'svg',
				{
					ref,
					xmlns: 'http://www.w3.org/2000/svg',
					width: dimension,
					height: dimension,
					fill,
					color: fill === 'currentColor' ? undefined : fill,
					viewBox: '0 0 24 24',
					transform: mirrored || contextMirrored ? 'scale(-1, 1)' : undefined,
					...contextProps,
					...props,
				},
				alt ? createElement('title', null, alt) : null,
				children,
				slashed ? createElement(SlashedGlyph, {style, glyph}) : glyph,
			);
		},
	);
	SolarIcon.displayName = displayName;
	return React.memo(SolarIcon);
}
